'use client';

import React, { memo, useMemo } from 'react';
import AppIcon from './AppIcon';

/** Intrinsic pixels of `public/assets/images/sopher-logo.png`. */
const LOGO_WIDTH = 1024;
const LOGO_HEIGHT = 576;

interface AppLogoProps {
  src?: string;
  iconName?: string;
  /** Target height in CSS pixels; width follows the logo aspect ratio. */
  size?: number;
  className?: string;
  /**
   * When true, render a lighter tone for dark/transparent nav.
   * When false, keep a darker/navy tone for light backgrounds.
   */
  onDarkBackground?: boolean;
  onClick?: () => void;
}

const AppLogo = memo(function AppLogo({
  src = '/assets/images/sopher-logo.png',
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onDarkBackground = false,
  onClick,
}: AppLogoProps) {
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  const imageClassName = useMemo(() => {
    const parts = ['block shrink-0 max-w-full h-auto w-auto object-contain bg-transparent'];
    if (onDarkBackground) {
      parts.push(
        // Top/transparent header: make logo light like nav text.
        '[filter:grayscale(1)_brightness(2.2)_contrast(1.05)_drop-shadow(0_0_8px_rgba(255,255,255,0.22))]'
      );
    } else {
      // Scrolled/white header: force a navy-blue tone.
      parts.push(
        '[filter:brightness(0)_saturate(100%)_invert(19%)_sepia(45%)_saturate(1657%)_hue-rotate(191deg)_brightness(96%)_contrast(90%)]'
      );
    }
    return parts.join(' ');
  }, [onDarkBackground]);

  return (
    <div className={containerClassName} onClick={onClick}>
      {src ? (
        // Native <img>: Next/Image optimization can flatten alpha or add a light matte on some PNGs.
        <img
          src={src}
          alt="Sopher — deer mark with SOPHER wordmark"
          width={LOGO_WIDTH}
          height={LOGO_HEIGHT}
          decoding="async"
          fetchPriority="high"
          className={imageClassName}
          style={{ height: size, width: 'auto' }}
        />
      ) : (
        <AppIcon name={iconName} size={size} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
